/**
 * One player's animation state machine — the per-tick half of `Humanoid::Process`
 * (humanoid.cpp:92-780).
 *
 * The shape of a tick is always the same:
 *
 *   recompute where I am  ->  advance a frame  ->  decide whether to pick a new clip
 *   ->  if so, pick one and re-integrate its motion  ->  touch the ball if this is the
 *   frame for it  ->  work out this frame's offsets  ->  publish a pose for the renderer
 *
 * The one genuinely surprising thing in here is that a player does not run toward the ball
 * and then kick it. He commits, up to a quarter of a second in advance, to an animation
 * whose foot will be in a particular place at a particular frame — and then the whole body
 * is quietly slid a few centimetres over the intervening frames so the foot and the ball
 * arrive together. That slide is the ACTION SMUGGLE, and without it nothing would ever
 * connect, because a 100 Hz simulation of a running man almost never puts a boot exactly
 * where a rolling ball is.
 */

import { Vector3 } from 'three';
import {
  absoluteAngle2D,
  clamp,
  fixAngle,
  modulateIntoRange,
  normalizedMax,
  normalizedOr,
  normalizedClamp,
  rotate2D,
  vecLength,
  wrapAngle,
} from '../../core/math';
import { seededRandom } from '../../core/random';
import { applyPose, createMovementHistory, type ApplyBuffer, type MovementHistory } from './apply';
import { numericVariable, variable, vectorVariable, type AnimClip } from './clip';
import {
  calculatePhysicsVector,
  movementAtFrame,
  type AnimConfig,
  type PlayerPhysics,
  DEFAULT_ANIM_CONFIG,
} from './physics';
import { calculateOutgoingMovement } from './physics';
import { QUADRANTS, getQuadrantID } from './quantize';
import {
  getIdleMovementAnimID,
  needTouch,
  selectCandidates,
  type PlayerCommand,
  type SelectionContext,
} from './select';
import { findReachableTouch, frontOfFootOffsetRel, type TouchSelection } from './touch';
import {
  ballControlVector,
  deflectVector,
  interfereVector,
  passVector,
  refinedShotDirection,
  shotVector,
  slidingVector,
  trapVector,
  type TouchContext,
  type TouchSpin,
} from './touchvector';
import { isKickedBodyPart } from './bodypart';
import {
  calculateFactualSpatialState,
  calculateSpatialState,
  createSpatialState,
  type SpatialState,
} from './spatial';
import { Foot } from './clip';
import { DRIBBLE_WALK_SWITCH, Velocity, floatToEnumVelocity } from './velocity';
import { TouchType, type Match, type MatchPlayer } from '../match';
import type { Skeleton } from '../skeleton';

const FORWARD = new Vector3(0, -1, 0);
const ZERO = new Vector3(0, 0, 0);

/** humanoid.cpp:56 — how long after a mid-clip requeue before another is allowed. */
const INITIAL_REQUEUE_DELAY_FRAMES = 22;
/** humanoid.cpp:57-59 — how late in a clip a requeue is still worth it. */
const MIN_REMAINING_MOVEMENT_REQUEUE_FRAMES = 6;
const MIN_REMAINING_TRAP_REQUEUE_FRAMES = 6;
const MAX_BALL_CONTROL_REQUEUE_FRAME = 8;
/** humanoid.cpp:55 — the most of the incoming body-angle error one clip may absorb. */
const BODY_ROTATION_SMOOTHING_MAX_ANGLE = 0.25 * Math.PI;
const BODY_ROTATION_SMOOTHING_FACTOR = 1.0;
/** humanoid.cpp:724 */
const BEGIN_ROTATION_FRAME_COUNT = 16;
/** gamedefines.hpp:59,64 */
const BALL_DISTANCE_OPTIMIZE_THRESHOLD = 10.0;
const DEFAULT_TOUCH_OFFSET_MS = 80;
/** humanoid.cpp — a mistimed touch this far out still counts, at reduced effect. */
const TOUCHABLE_DISTANCE = 0.4;
/** The most the movement smuggle may drag a player, in metres per second. */
const MAX_SMUGGLE_MPS = 1.6;
/**
 * How far ahead of himself a computer player aims a shot. It has to match the lead
 * `shotCommand` in `src/sim/controller.ts` uses when it works the direction out, because
 * re-aiming the shot at contact has to start from the same place the aim started from.
 */
const SHOT_AIM_LEAD_SECONDS = 0.2;

enum Interrupt {
  None,
  Switch,
  ReQueue,
  Trip,
  Struck,
}

interface CurrentAnim {
  clip: AnimClip;
  clipIndex: number;
  /** From the COMMAND, not from the clip's own type. */
  functionType: string;
  frameNum: number;
  touchFrame: number;
  /** Where the ball will be at the contact frame, world space, with height. */
  touchPos: Vector3;
  /** The re-integrated path, relative to `startPos`, already in world orientation. */
  positions: Vector3[];
  incomingMovement: Vector3;
  outgoingMovement: Vector3;
  /** Blended out across the clip's first frames; the body-angle error carried in. */
  rotationSmuggleBegin: number;
  /** Blended in across the same window; the steering correction from the solver. */
  rotationSmuggleEnd: number;
  rotationSmuggleOffset: number;
  fullActionSmuggle: Vector3;
  actionSmuggle: Vector3;
  actionSmuggleOffset: Vector3;
  movementSmuggle: Vector3;
  movementSmuggleOffset: Vector3;
  /**
   * How far this clip has been shoved sideways by collisions since it started.
   *
   * Not the same thing as the player's own `decayingPositionOffset`, and the original keeps
   * both (`humanoidbase.cpp:1027-1043` bumps them together). The player's one is capped at ten
   * centimetres and fades by a twentieth a tick, which is right for the things that ask how
   * unbalanced a man is — how much power he can put through the ball, how badly his aim
   * suffers. This one is neither capped nor faded and is thrown away when the next clip starts,
   * because the question it answers is different: the foot was aimed at a spot chosen a quarter
   * of a second ago, and this is how far the whole body has been pushed off it since. Only
   * `handleBallContact` reads it.
   */
  positionOffset: Vector3;
  originatingCommand: PlayerCommand | null;
}

function blankAnim(clip: AnimClip, clipIndex: number): CurrentAnim {
  return {
    clip,
    clipIndex,
    functionType: 'movement',
    frameNum: 0,
    touchFrame: -1,
    touchPos: new Vector3(),
    positions: [],
    incomingMovement: new Vector3(),
    outgoingMovement: new Vector3(),
    rotationSmuggleBegin: 0,
    rotationSmuggleEnd: 0,
    rotationSmuggleOffset: 0,
    fullActionSmuggle: new Vector3(),
    actionSmuggle: new Vector3(),
    actionSmuggleOffset: new Vector3(),
    movementSmuggle: new Vector3(),
    movementSmuggleOffset: new Vector3(),
    positionOffset: new Vector3(),
    originatingCommand: null,
  };
}

export interface HumanoidAnimOptions {
  library: AnimClip[];
  player: PlayerPhysics;
  config?: AnimConfig;
  /** Where he starts, on the pitch plane. */
  position: Vector3;
  /** What he starts out looking at. */
  focus: Vector3;
  /** Standing height in metres — the clip's ball height is scaled by it. */
  height?: number;
  /**
   * Where the chance in a touch comes from: how badly a trap is scuffed, how far a shot
   * curls, which way a mistimed clearance goes.
   *
   * A STREAM, and it must be repeatable. The original draws these from the engine's own
   * generator, seeded from the clock, so no two of its runs agree. Ours defaults to a seeded
   * stream so that a run without an explicit seed is still a run somebody can repeat; the
   * match passes each player his own, derived from the run's seed and the player's database
   * id, so two players never draw the same sequence.
   */
  random?: () => number;
  /**
   * Which frame of his idle clip he begins on, 0 to 1, so a whole team is not breathing in
   * lockstep. Derived from the player's database id rather than drawn, which is a deliberate
   * divergence: it keeps every screenshot and every trace comparison identical. Falls back to
   * the first number off `random` when not given, which is what this used to do.
   */
  idleFrameBias?: number;
}

/**
 * The animation runtime for one player.
 *
 * Owns the current clip, the spatial state, and the pose. Does NOT own the renderer: call
 * `writePose` with a skeleton when you want the current frame written onto it.
 */
export class HumanoidAnim {
  readonly spatial: SpatialState = createSpatialState();

  private readonly library: AnimClip[];
  private readonly player: PlayerPhysics;
  private readonly config: AnimConfig;
  private readonly height: number;
  private readonly random: () => number;
  private readonly history: MovementHistory = createMovementHistory();

  private current: CurrentAnim;
  private previousFunctionType = 'movement';

  private startPos = new Vector3();
  private startAngle = 0;
  private previousPosition2D = new Vector3();
  private reQueueDelayFrames = 0;
  private pendingTrip: { direction: Vector3; type: number } | null = null;
  /** Same shape, for `struck` — see the note there. Never both set at once. */
  private pendingStruck: { direction: Vector3 } | null = null;

  /**
   * The match, and this runtime's own player in it, kept from the last tick.
   *
   * `HumanoidBase` holds both as members, which is how `TripMe` can ask on the spot whether
   * this player happens to be holding the ball (`humanoidbase.cpp:1045-1046`). Here they
   * arrive once a tick instead, so they are remembered. Nothing reads them before the first
   * tick, and the only thing that ever asks for a trip — the collision pass — runs after
   * every player has ticked (`match.cpp:1836-1922`), so what is remembered is this tick's.
   */
  private match: Match | null = null;
  private matchPlayer: MatchPlayer | null = null;

  /** Where he will be, and which way he will face, when this clip finishes. */
  private nextStartPos = new Vector3();
  private nextStartAngle = 0;

  /** What the renderer should draw. Rebuilt every tick. */
  private buffer: ApplyBuffer;

  constructor(options: HumanoidAnimOptions) {
    this.library = options.library;
    this.player = options.player;
    this.config = options.config ?? DEFAULT_ANIM_CONFIG;
    this.height = options.height ?? 1.92;
    // Never the platform's own generator: an unrepeatable run cannot be compared with the
    // original game, and that comparison is the point of the whole harness.
    this.random = options.random ?? seededRandom(0);

    const idleIndex = getIdleMovementAnimID(this.library, this.spatial);
    this.current = blankAnim(this.library[idleIndex]!, idleIndex);
    this.buffer = {
      clip: this.current.clip,
      frameNum: 0,
      orientation: 0,
      noPos: true,
      smooth: false,
      smoothFactor: 0,
      timeDiffMs: 10,
    };

    this.resetPosition(
      options.position,
      options.focus,
      options.idleFrameBias ?? (options.random ? options.random() : 0),
    );
  }

  get clip(): AnimClip {
    return this.current.clip;
  }

  get frameNum(): number {
    return this.current.frameNum;
  }

  /** What the player is currently DOING — movement, ballcontrol, trap. */
  get currentFunctionType(): string {
    return this.current.functionType;
  }

  /** The frame this clip meets the ball on, or -1. */
  get touchFrame(): number {
    return this.current.touchFrame;
  }

  get touchPosition(): Vector3 {
    return this.current.touchPos;
  }

  /** How tall this man is, in metres. The AI reads it to know how high a ball he can play. */
  get standingHeight(): number {
    return this.height;
  }

  /** World position on the pitch plane. */
  get position(): Vector3 {
    return this.spatial.position;
  }

  /** World heading in radians, including this frame's rotation smuggle. */
  get orientation(): number {
    return this.buffer.orientation;
  }

  /** `HumanoidBase::OffsetPosition` — collision displacement is part of factual motion. */
  offsetPosition(offset: Vector3): void {
    const planar = offset.clone().setZ(0);
    this.nextStartPos.add(planar);
    this.startPos.add(planar);
    this.spatial.position.add(planar);
    this.spatial.positionOffsetMovement.addScaledVector(planar, 100);
    this.player.decayingPositionOffset.add(planar);
    if (this.player.decayingPositionOffset.length() > 0.1) {
      this.player.decayingPositionOffset.setLength(0.1);
    }
    // The same shove, kept a second time without the cap or the fade, for the one question
    // that needs the whole of it: whether the foot still reaches the ball.
    this.current.positionOffset.add(planar);
  }

  /** Queue the forced interrupt used by both proximity trips and tackle hits. */
  trip(direction: Vector3, type: number): void {
    // A man holding the ball is never brought down, and the request is THROWN AWAY rather
    // than held back until he lets go — it is the very first line of `TripMe`
    // (`humanoidbase.cpp:1046`). The referee hears about the challenge either way, on the
    // collision's own line (`match.cpp:1816`), so all that is lost is the falling over.
    // Queueing it instead makes a keeper collapse the instant he throws the ball out, for a
    // bump that happened up to four seconds earlier and that nobody is being punished for.
    if (this.match !== null && this.match.ballRetainer === this.matchPlayer) return;
    if (variable(this.current.clip, 'incoming_special_state') !== '') return;
    if (variable(this.current.clip, 'outgoing_special_state') !== '') return;
    if (this.current.functionType === 'sliding') return;
    if (
      this.pendingTrip === null &&
      (this.current.functionType !== 'trip' ||
        (variable(this.current.clip, 'triptype') === '1' && type > 1))
    ) {
      this.pendingTrip = { direction: normalizedOr(direction, this.spatial.directionVec), type };
    }
  }

  /**
   * Queue the forced interrupt for a man who has just been punched or flying-kicked —
   * tranche 2.3 of `docs/design/violence-and-variety.md`.
   *
   * The engine's only other forced fall is `trip`, and it is the wrong shape for this: the
   * whole `trip` family is a man whose LEGS met another man's legs (38 clips, three
   * severities), which is not what a fist or a boot to the body looks like. This plays one of
   * the two `special/struck_*` clips instead — `specialvar1` 6, with `specialvar2` 1 for a
   * standing victim and 2 for a running one, chosen from his OWN velocity at promotion time
   * by `struckCommands` below, not by the caller — and, because a special clip is played
   * EXACTLY AS DRAWN (`physics.ts:233`, physics bias 0.0), its own root track is the
   * knockback: there is no solver underneath it to bend.
   *
   * The guards mirror `trip`'s own for the same reasons: a man holding the ball is never
   * knocked down here either (the collision that caused this is told to the referee on its
   * own line regardless), and a clip already mid `incoming_special_state` /
   * `outgoing_special_state` chain — getting up, taking a throw-in stance — is left to
   * finish rather than cut into.
   */
  struck(direction: Vector3): void {
    if (this.match !== null && this.match.ballRetainer === this.matchPlayer) return;
    if (variable(this.current.clip, 'incoming_special_state') !== '') return;
    if (variable(this.current.clip, 'outgoing_special_state') !== '') return;
    if (this.current.functionType === 'sliding') return;
    if (this.pendingTrip === null && this.pendingStruck === null) {
      this.pendingStruck = { direction: normalizedOr(direction, this.spatial.directionVec) };
    }
  }

  /**
   * Place the player and give him a clip to stand in — `HumanoidBase::ResetPosition`
   * (humanoidbase.cpp:929-1025).
   */
  resetPosition(position: Vector3, focus: Vector3, idleFrameBias = 0): void {
    this.startPos.copy(position);
    this.startPos.z = 0;
    // Face whatever he has been told to look at; if that is exactly where he stands,
    // fall back to plain forward rather than to whatever atan2 makes of a zero vector.
    this.startAngle = wrapAngle(
      fixAngle(absoluteAngle2D(normalizedOr(focus.clone().sub(this.startPos), FORWARD))),
    );

    this.previousPosition2D.copy(this.startPos);

    // A trip that was asked for but never played is thrown away with everything else:
    // `ResetPosition` clears the pending interrupt and the trip type
    // (`humanoidbase.cpp:1006-1007`). Without this a challenge from just before a stoppage
    // lands on the player while the set piece is being arranged, minutes after the referee
    // has finished with it.
    this.pendingTrip = null;
    // Same reasoning as `pendingTrip`, for a struck reaction asked for just before a
    // restart: he is teleported into a fresh idle clip along with everyone else, not left to
    // stagger into it.
    this.pendingStruck = null;

    this.spatial.position.copy(this.startPos);
    this.spatial.angle = this.startAngle;
    rotate2D(FORWARD, this.startAngle, this.spatial.directionVec);
    this.spatial.floatVelocity = 0;
    this.spatial.enumVelocity = Velocity.Idle;
    this.spatial.movement.set(0, 0, 0);
    this.spatial.actualMovement.set(0, 0, 0);
    this.spatial.physicsMovement.set(0, 0, 0);
    this.spatial.animMovement.set(0, 0, 0);
    this.spatial.actionSmuggleMovement.set(0, 0, 0);
    this.spatial.movementSmuggleMovement.set(0, 0, 0);
    this.spatial.positionOffsetMovement.set(0, 0, 0);
    this.spatial.relBodyDirectionVec.copy(FORWARD);
    this.spatial.relBodyDirectionVecNonquantized.copy(FORWARD);
    this.spatial.relBodyAngle = 0;
    this.spatial.relBodyAngleNonquantized = 0;
    rotate2D(FORWARD, this.startAngle, this.spatial.bodyDirectionVec);
    this.spatial.bodyAngle = 0;
    this.spatial.foot = Foot.Right;

    const idleIndex = getIdleMovementAnimID(this.library, this.spatial);
    const clip = this.library[idleIndex]!;
    this.current = blankAnim(clip, idleIndex);
    this.current.positions = clip.positionCache.map((p) => p.clone());
    // A varied start frame so a whole team is not breathing in lockstep.
    this.current.frameNum = Math.min(
      Math.max(0, Math.floor(idleFrameBias * Math.max(1, clip.effectiveFrameCount))),
      Math.max(0, clip.effectiveFrameCount - 1),
    );

    this.nextStartPos.copy(this.startPos);
    this.nextStartAngle = this.startAngle;

    this.buffer.clip = clip;
    this.buffer.frameNum = this.current.frameNum;
    this.buffer.orientation = this.startAngle;
    this.buffer.noPos = true;
    this.buffer.smooth = false;
    this.buffer.smoothFactor = 0;
  }

  /** `HumanoidBase::SelectRetainAnim`, used when a throw-in taker is placed. */
  selectRetainAnim(): void {
    const clipIndex = this.library.findIndex(
      (clip) =>
        clip.animType === 'movement' &&
        clip.incomingVelocityEnum === Velocity.Idle &&
        clip.outgoingVelocityEnum === Velocity.Idle &&
        clip.currentFoot === this.spatial.foot &&
        variable(clip, 'incoming_special_state') === '' &&
        variable(clip, 'incoming_retain_state') !== '' &&
        variable(clip, 'incoming_retain_state') === variable(clip, 'outgoing_retain_state') &&
        clip.incomingBodyAngle === 0 &&
        clip.outgoingAngle === 0,
    );
    if (clipIndex < 0) throw new Error('Animation library has no stationary retained-ball clip');

    const clip = this.library[clipIndex]!;
    this.current = blankAnim(clip, clipIndex);
    this.current.positions = clip.positionCache.map((position) => position.clone());
    this.nextStartPos.copy(this.startPos);
    this.nextStartAngle = this.startAngle;
    this.buffer.clip = clip;
    this.buffer.frameNum = 0;
    this.buffer.orientation = this.startAngle;
    this.buffer.noPos = true;
    this.buffer.smooth = false;
    this.buffer.smoothFactor = 0;
  }

  /**
   * One 10 ms tick.
   *
   * `requestCommand` is asked for an ordered list of things the player would like to do;
   * the first one for which a clip can be found wins. A player near the ball typically asks
   * for a dribble touch, then a trap, then plain movement, and takes whichever the library
   * and the geometry allow.
   */
  tick(match: Match, self: MatchPlayer, requestCommand: () => PlayerCommand[]): void {
    this.match = match;
    this.matchPlayer = self;
    this.player.decayingPositionOffset.multiplyScalar(0.95);
    calculateSpatialState(this.spatial, {
      clip: this.current.clip,
      positions: this.current.positions,
      frameNum: this.current.frameNum,
      startPos: this.startPos,
      startAngle: this.startAngle,
      previousPosition2D: this.previousPosition2D,
      actionSmuggleOffset: this.current.actionSmuggleOffset,
      actionSmuggleSustainOffset: ZERO,
      movementSmuggleOffset: this.current.movementSmuggleOffset,
      rotationSmuggleOffset: this.current.rotationSmuggleOffset,
    });
    this.previousPosition2D.copy(this.spatial.position);
    this.spatial.positionOffsetMovement.set(0, 0, 0);

    this.current.frameNum++;

    let interrupt = Interrupt.None;

    // Whatever was asked for since the last tick plays now. There is no test for holding
    // the ball here: that one ran when the trip was requested. A trip is never carried over
    // to a later tick either — the original wipes `interruptAnim` at the end of every
    // `Process` (`humanoidbase.cpp:674`), so one it cannot play is simply lost.
    if (this.pendingTrip !== null) interrupt = Interrupt.Trip;
    // A struck reaction loses the tie-break on the rare tick both are queued — `struck`
    // itself already refuses to queue over an existing `pendingTrip`, so this only bites
    // when a SECOND, unrelated collision calls `trip` on the same player in the same tick.
    else if (this.pendingStruck !== null) interrupt = Interrupt.Struck;

    // A clip runs out on its LAST frame, not one past it: the last frame of one clip and
    // the first of the next are treated as the same instant.
    if (interrupt === Interrupt.None && this.current.frameNum === this.current.clip.frameCount - 1) {
      interrupt = Interrupt.Switch;
    }

    // The opportunistic mid-clip requeue, rate-limited on match wall-clock rather than on
    // anything about this player. The modulo staggers the twenty-two players against each
    // other, which is both a performance measure and a feel measure: they do not all change
    // their minds on the same tick. The man on the ball gets to reconsider five times more
    // often than a defender forty metres away.
    let mayReQueue = interrupt === Interrupt.None;
    if (mayReQueue) {
      const actionDistance = _scratch
        .copy(this.spatial.position)
        .addScaledVector(this.spatial.movement, 0.1)
        .distanceTo(_ball2D.copy(match.ball.predict(100, _ballProbe)).setZ(0));
      const t = match.timeMs;
      const tid = self.teamId;
      const isDesignated = match.designatedPossessionPlayer === self;
      const isTeamDesignated = match.team(self.teamId).designatedPlayer === self;
      if (isDesignated && actionDistance < 3.0) mayReQueue = (t + tid * 10) % 20 === 0;
      else if (isDesignated) mayReQueue = (t + tid * 10) % 30 === 0;
      else if (isTeamDesignated) mayReQueue = (t + tid * 20) % 40 === 0;
      else if (actionDistance < 5.0) mayReQueue = (t + tid * 20) % 50 === 0;
      else if (actionDistance < 10.0) mayReQueue = (t + tid * 40) % 80 === 0;
      else mayReQueue = false;
    }

    if (mayReQueue) {
      const image = match.mentalImageFor(self);
      const ballDistance = this.spatial.position.distanceTo(
        _ball2D.copy(image.ballPrediction(500, _ballProbe)).setZ(0),
      );
      const type = this.current.functionType;
      const touchPending = this.current.frameNum < this.current.touchFrame;
      mayReQueue =
        ((type === 'movement' && !self.hasPossession && ballDistance < 16.0) ||
          (type === 'movement' && self.hasPossession) ||
          (type === 'trap' && touchPending) ||
          (type === 'ballcontrol' && touchPending)) &&
        variable(this.current.clip, 'incoming_special_state') === '' &&
        variable(this.current.clip, 'outgoing_special_state') === '';
    }

    if (mayReQueue) interrupt = Interrupt.ReQueue;

    if (interrupt !== Interrupt.None) {
      const trip = this.pendingTrip;
      const struck = this.pendingStruck;
      const commands =
        interrupt === Interrupt.Trip && trip
          ? tripCommands(trip.direction, trip.type, this.spatial.floatVelocity)
          : interrupt === Interrupt.Struck && struck
            ? struckCommands(struck.direction, this.spatial.enumVelocity, this.spatial.floatVelocity)
            : requestCommand();
      if (interrupt === Interrupt.Trip) this.pendingTrip = null;
      if (interrupt === Interrupt.Struck) this.pendingStruck = null;
      let found = false;
      let preferPassAndShot = false;
      for (const command of commands) {
        const type = command.desiredFunctionType;
        if (type === 'shortpass' || type === 'longpass' || type === 'highpass' || type === 'shot') {
          preferPassAndShot = true;
        }
        found = this.selectAnim(command, interrupt, match, self, preferPassAndShot);
        if (found) break;
      }
      if (
        !found &&
        (interrupt === Interrupt.Switch || interrupt === Interrupt.Trip || interrupt === Interrupt.Struck)
      ) {
        // The original treats this as fatal in a debug build and re-places the player in a
        // release one. Re-placing is the survivable choice; it should never happen, so it
        // is worth knowing about when it does.
        console.warn('[gf] no applicable animation found; re-placing player');
        this.resetPosition(this.spatial.position, match.ball.predict(0, _ballProbe));
        return;
      }
    }

    this.reQueueDelayFrames = Math.max(this.reQueueDelayFrames - 1, 0);

    this.handleBallContact(match, self);
    this.releaseRetainedBall(match, self);
    this.updateSmuggles();
    this.publish();
  }

  /**
   * Let go of a held ball the moment the clip stops declaring that it is being held —
   * `humanoid.cpp:652-665`.
   *
   * A clip states what it does with the ball on either side of its contact frame. A keeper's
   * catch ends holding it; the hold-ball walk clips both start and end holding it; a throw
   * starts holding it and ends not. So the test is which side of the contact frame we are on:
   * before it, the incoming state decides, after it the outgoing one, and a clip that holds on
   * both sides always holds. The throw therefore releases on exactly the frame it strikes,
   * which is why this has to run after the contact code and not before it.
   *
   * Without this the retainer is never cleared in open play, and since only hold-ball and pass
   * clips can follow a catch, the keeper walks around with the ball forever.
   *
   * While he is still holding it, the ball counts as freshly and deliberately handled on every
   * single tick (`humanoid.cpp:660`). That is not bookkeeping for its own sake: without it the
   * touch ages out mid-hold and every rule that asks who last had the ball — the back-pass
   * test among them — comes to believe nobody has touched it in a while.
   */
  private releaseRetainedBall(match: Match, self: MatchPlayer): void {
    if (match.ballRetainer !== self) return;
    const anim = this.current;
    const incoming = variable(anim.clip, 'incoming_retain_state') !== '';
    const outgoing = variable(anim.clip, 'outgoing_retain_state') !== '';
    const stillHolding =
      (anim.touchFrame <= anim.frameNum && outgoing) ||
      (anim.touchFrame > anim.frameNum && incoming) ||
      (incoming && outgoing);
    if (stillHolding) match.setLastTouchPlayer(self, TouchType.IntentionalNonkicked);
    else match.ballRetainer = null;
  }

  /** humanoid.cpp:1159-1820. */
  private selectAnim(
    command: PlayerCommand,
    interrupt: Interrupt,
    match: Match,
    self: MatchPlayer,
    preferPassAndShot: boolean,
  ): boolean {
    const type = command.desiredFunctionType;
    const isMovement = type === 'movement';
    const image = match.mentalImageFor(self);

    // ---- cheap rejections, so twenty-two players do not each run the whole selector ----
    if (type !== 'movement' && type !== 'trip' && type !== 'special' && type !== 'sliding') {
      const soon = _ball2D.copy(image.ballPrediction(200, _ballProbe)).setZ(0);
      if (soon.distanceTo(this.spatial.position) > BALL_DISTANCE_OPTIMIZE_THRESHOLD) return false;

      const atTouch = _ball2D.copy(image.ballPrediction(DEFAULT_TOUCH_OFFSET_MS, _ballProbe)).setZ(0);
      if (atTouch.distanceTo(this.spatial.position) > 2.0) {
        // The ball is getting further away rather than closer: not worth chasing with a
        // touch animation.
        const aheadOfMe = _scratch
          .copy(this.spatial.position)
          .addScaledVector(this.spatial.movement, DEFAULT_TOUCH_OFFSET_MS * 0.001);
        const now = _ball2Db.copy(image.ballPrediction(0, _ballProbe)).setZ(0);
        if (atTouch.distanceTo(aheadOfMe) > now.distanceTo(this.spatial.position)) return false;
      }

      // What he believes has drifted too far from the truth to act on — somebody has
      // played the ball and he has not seen it yet.
      if (type !== 'deflect' && match.ballRetainer !== self) {
        const believed = image.ballPrediction(1000, _ballProbe);
        if (believed.distanceTo(match.ball.predict(1000, _ballProbeB)) > 2.0) return false;
      }
    }

    if (interrupt === Interrupt.ReQueue) {
      const currentType = this.current.functionType;
      const focusDistance = match.designatedPossessionPlayer.position.distanceTo(
        this.spatial.position,
      );
      const touchFrame = this.current.touchFrame;

      // Never drop a ball action to go back to plain running.
      if (currentType !== 'movement' && isMovement) return false;
      if (currentType === 'movement' && isMovement) {
        if (self.hasPossession || focusDistance > 12.0) return false;
        if (
          this.current.frameNum + MIN_REMAINING_MOVEMENT_REQUEUE_FRAMES >
          this.current.clip.effectiveFrameCount
        ) {
          return false;
        }
        if (this.reQueueDelayFrames > 0) return false;
      }
      if (currentType === 'ballcontrol' && type === 'ballcontrol') {
        if (this.current.frameNum > MAX_BALL_CONTROL_REQUEUE_FRAME) return false;
        if (this.reQueueDelayFrames > 0) return false;
      }
      if (currentType === 'ballcontrol' && type === 'trap') return false;
      if (currentType === 'trap' && (type === 'trap' || type === 'ballcontrol')) {
        if (this.current.frameNum + MIN_REMAINING_TRAP_REQUEUE_FRAMES > touchFrame) return false;
        if (this.reQueueDelayFrames > 0) return false;
      }

      // Do not bother switching to something barely different from what is already running.
      const origin = this.current.originatingCommand;
      if (
        origin &&
        origin.desiredFunctionType === type &&
        origin.desiredDirection
          .clone()
          .multiplyScalar(origin.desiredVelocityFloat)
          .sub(command.desiredDirection.clone().multiplyScalar(command.desiredVelocityFloat))
          .length() < 1.5
      ) {
        return false;
      }

      // ...nor if the change of momentum we already planned is close enough to the one
      // now being asked for.
      const pairs =
        (currentType === 'movement' && isMovement) ||
        (currentType === 'ballcontrol' && type === 'ballcontrol') ||
        (currentType === 'trap' && (type === 'ballcontrol' || type === 'trap'));
      if (pairs) {
        const plannedMomentumChange = this.current.outgoingMovement
          .clone()
          .sub(this.current.incomingMovement);
        const desiredMomentumChange = command.desiredDirection
          .clone()
          .multiplyScalar(command.desiredVelocityFloat)
          .sub(this.spatial.movement);
        const dot = desiredMomentumChange.dot(plannedMomentumChange);
        const distance = desiredMomentumChange.clone().sub(plannedMomentumChange).length();
        if ((dot > 0 && distance < 4.0) || dot > 0.8 || distance < 2.0) return false;
      }

      // He kicked it moments ago and still has it: let the ball run rather than fussing
      // at it again, unless he genuinely wants to go somewhere else.
      if (
        currentType === 'movement' &&
        type === 'ballcontrol' &&
        match.timeMs - self.lastTouchTimeMs < 600 &&
        self.lastTouchType === TouchType.IntentionalKicked &&
        self.hasPossession
      ) {
        const change = this.spatial.movement
          .clone()
          .sub(command.desiredDirection.clone().multiplyScalar(command.desiredVelocityFloat))
          .length();
        if (change < 1.0) return false;
      }
    }

    if (interrupt !== Interrupt.ReQueue || this.current.frameNum > 12) {
      calculateFactualSpatialState(this.spatial, this.current.clip);
    }

    // Which way the ball is arriving, in his own frame — a trap clip is authored for one.
    const ballIncomingDirectionRel = rotate2D(
      _scratch
        .copy(image.ballPrediction(180, _ballProbe))
        .sub(image.ballPrediction(120, _ballProbeB)),
      -this.spatial.angle,
      _ballIncoming,
    );
    if (vecLength(ballIncomingDirectionRel) === 0) ballIncomingDirectionRel.set(0, 0, 0);
    else ballIncomingDirectionRel.normalize();

    const context: SelectionContext = {
      library: this.library,
      spatial: this.spatial,
      currentClip: this.current.clip,
      inPlay: match.inPlay,
      inSetPiece: match.inSetPiece,
      ballDistance: this.spatial.position.distanceTo(
        _ball2D.copy(match.ball.predict(200, _ballProbe)).setZ(0),
      ),
      allowLastDitch: match.allowLastDitch(self),
      allowLastDitchIgnoringPossession: match.allowLastDitch(self, false),
      highOrBouncyBall: match.highOrBouncyBall(),
      isBallRetainer: match.isRetainingBall(self),
      ballIncomingDirectionRel,
      quadrantOf: (id) => {
        const q = QUADRANTS[id] ?? QUADRANTS[0]!;
        return { velocity: q.velocity as Velocity, angle: q.angle };
      },
    };

    const candidates = selectCandidates(context, command);
    if (!candidates) return false;

    const desiredMovement = command.desiredDirection
      .clone()
      .multiplyScalar(command.desiredVelocityFloat);

    let clipIndex: number;
    let positions: Vector3[];
    let rotationOffset: number;
    let touchFrame = -1;
    let touchPos = new Vector3();
    let fullActionSmuggle = new Vector3();
    let actionSmuggle = new Vector3();

    if (isMovement || type === 'trip' || type === 'special') {
      clipIndex = candidates.dataSet[0]!;
      const solved = calculatePhysicsVector(
        this.library[clipIndex]!,
        command.useDesiredMovement,
        desiredMovement,
        candidates.desiredBodyDirectionRel,
        this.spatial,
        this.player,
        this.config,
      );
      positions = solved.positions;
      rotationOffset = solved.rotationOffset;
    } else {
      // Every ball action goes through the reachability search, which is allowed to reject
      // every candidate — that is how "I cannot get to this ball" is expressed.
      if (type === 'ballcontrol') {
        const head = this.library[candidates.dataSet[0]!]!;
        const ballMovement = _scratch
          .copy(image.ballPrediction(250, _ballProbe))
          .sub(image.ballPrediction(240, _ballProbeB))
          .multiplyScalar(100);
        ballMovement.z = 0;
        if (!needTouch(head, command, this.spatial, ballMovement, vecLength(match.ball.momentum))) {
          return false;
        }
      }

      const found: TouchSelection | null = findReachableTouch(
        {
          library: this.library,
          spatial: this.spatial,
          player: this.player,
          config: this.config,
          playerHeight: this.height,
          ballPrediction: (ms, out) => image.ballPrediction(ms, out),
          ballPredictionTrue: (ms, out) => match.ball.predict(ms, out),
          lastTouchBias: (decayMs, atMs) => self.lastTouchBias(decayMs, atMs),
          nowMs: match.timeMs,
          closestOpponentDistance: self.closestOpponentDistance,
          isBallRetainer: match.isRetainingBall(self),
          someoneElseRetainsBall: match.ballRetainer !== null && match.ballRetainer !== self,
          preferPassAndShot,
        },
        candidates.dataSet,
        command.useDesiredMovement,
        command.desiredDirection,
        command.desiredVelocityFloat,
        candidates.desiredBodyDirectionRel,
      );
      if (!found) {
        // A SLIDING TACKLE GOES IN WHETHER OR NOT IT CAN REACH THE BALL, and the two halves
        // of that only make sense together. The reach test grants a slide a fifth of the
        // stretch it grants anything else, with the original's own note beside the number
        // saying why — "prefer sliding without ball touch" (`humanoid.cpp:2176`) — so a slide
        // that wins the ball cleanly is meant to be the exception. Then this: when no clip
        // can reach it, the first candidate is played anyway, as a slide with no ball contact
        // at all (`humanoid.cpp:1711-1719`).
        //
        // That is the whole supply of fouls in the game. The referee treats a slide that
        // touched the ball as fair and one that did not as a foul (`referee.cpp:344-360`), so
        // dropping this branch — as this port did — takes away every sliding tackle AND every
        // free kick with it, while leaving in place the deliberate handicap that was only
        // ever there because of it. Measured before it went back: zero slides and zero fouls
        // in six matches.
        if (type !== 'sliding' || candidates.dataSet.length === 0) return false;
        clipIndex = candidates.dataSet[0]!;
        const solved = calculatePhysicsVector(
          this.library[clipIndex]!,
          command.useDesiredMovement,
          desiredMovement,
          candidates.desiredBodyDirectionRel,
          this.spatial,
          this.player,
          this.config,
        );
        positions = solved.positions;
        rotationOffset = solved.rotationOffset;
      } else {
        clipIndex = found.clipIndex;
        positions = found.positions;
        rotationOffset = found.rotationOffset;
        touchFrame = found.touchFrame;
        touchPos = found.touchPos;
        fullActionSmuggle = found.fullActionSmuggle;
        actionSmuggle = found.actionSmuggle;
      }
    }

    const nextClip = this.library[clipIndex]!;

    // Do not requeue into the same manoeuvre we are already performing — it would look
    // like a stutter and achieve nothing.
    if (interrupt === Interrupt.ReQueue && this.current.positions.length > 1 && positions.length > 1) {
      const currentOutIdle = this.current.clip.outgoingVelocityEnum === Velocity.Idle;
      const nextOutIdle = nextClip.outgoingVelocityEnum === Velocity.Idle;
      const sameQuadrant = !currentOutIdle && this.current.clip.quadrantId === nextClip.quadrantId;
      const bothStopping =
        currentOutIdle &&
        nextOutIdle &&
        Math.abs(this.current.clip.outgoingAngle - nextClip.outgoingAngle) < 0.06 * Math.PI;
      if (this.current.functionType === type && (sameQuadrant || bothStopping)) {
        return false;
      }
    }

    // ---- commit ----------------------------------------------------------
    this.previousFunctionType = this.current.functionType;
    const previousIncoming = this.spatial.movement.clone();

    this.current = blankAnim(nextClip, clipIndex);
    this.current.functionType = type;
    this.current.touchFrame = touchFrame;
    this.current.touchPos.copy(touchPos);
    this.current.incomingMovement.copy(previousIncoming);
    this.current.outgoingMovement.copy(calculateOutgoingMovement(positions));
    this.current.positions = positions;
    this.current.originatingCommand = command;
    this.current.fullActionSmuggle.copy(fullActionSmuggle);
    this.current.actionSmuggle.copy(actionSmuggle);

    const maxAngle = BODY_ROTATION_SMOOTHING_MAX_ANGLE * (isMovement ? 1.0 : 0.5);
    this.current.rotationSmuggleBegin = clamp(
      wrapAngle(this.spatial.relBodyAngleNonquantized - nextClip.incomingBodyAngle) *
        BODY_ROTATION_SMOOTHING_FACTOR,
      -maxAngle,
      maxAngle,
    );
    this.current.rotationSmuggleEnd = rotationOffset;

    this.startPos.copy(this.spatial.position);
    this.startAngle = this.spatial.angle;
    this.calculatePredictedSituation();

    this.current.movementSmuggle.copy(this.calculateMovementSmuggle(match, self));

    this.buffer.clip = nextClip;
    this.buffer.smooth = true;
    // A natural movement-to-movement switch needs no smoothing at all: consecutive
    // movement clips were authored to line up. Everything else gets most of it.
    //
    // The original writes this as five assignments in a row where the last one to match
    // wins (`humanoid.cpp:277-284`); the seven types below are every one it names. Nothing
    // in that list can also be movement, so an if/else chain gives the same answers. A
    // LONG pass is deliberately absent and keeps the full 1.0 — the only strike that does.
    if (interrupt === Interrupt.Switch && this.previousFunctionType === 'movement' && isMovement) {
      this.buffer.smoothFactor = 0.0;
    } else if (
      type === 'shot' ||
      type === 'shortpass' ||
      type === 'highpass' ||
      type === 'deflect' ||
      type === 'sliding' ||
      type === 'ballcontrol' ||
      type === 'trap'
    ) {
      this.buffer.smoothFactor = 0.8;
    } else {
      this.buffer.smoothFactor = 1.0;
    }

    if (interrupt === Interrupt.ReQueue && this.previousFunctionType === type) {
      this.reQueueDelayFrames = INITIAL_REQUEUE_DELAY_FRAMES;
    }

    return true;
  }

  /** humanoidbase.cpp:1603-1615 — where this clip will leave him. */
  private calculatePredictedSituation(): void {
    const anim = this.current;
    if (anim.positions.length > anim.frameNum) {
      this.nextStartPos
        .copy(this.spatial.position)
        .add(anim.positions[anim.clip.effectiveFrameCount] ?? anim.positions[anim.positions.length - 1]!)
        .add(anim.actionSmuggle)
        .add(anim.movementSmuggle);
    } else {
      this.nextStartPos
        .copy(this.spatial.position)
        .add(rotate2D(anim.clip.translation, this.spatial.angle, _scratch))
        .add(anim.actionSmuggle)
        .add(anim.movementSmuggle);
    }
    this.nextStartPos.z = 0;
    this.nextStartAngle = modulateIntoRange(
      -Math.PI,
      Math.PI,
      this.spatial.angle + anim.clip.outgoingAngle + anim.rotationSmuggleEnd,
    );
  }

  /**
   * The gentle drift that leaves a dribbler perfectly placed for his next touch —
   * `CalculateMovementSmuggle` (humanoid.cpp:2331-2450).
   *
   * Only the one player the match has designated gets it, and only while he is running
   * rather than touching the ball. Without possession it works sideways onto the ball's
   * line of travel, which is how a player drifts across to meet a pass instead of arriving
   * behind it; with possession it simply keeps the ball in front of his feet.
   */
  private calculateMovementSmuggle(match: Match, self: MatchPlayer): Vector3 {
    const out = _smuggleOut.set(0, 0, 0);
    const anim = this.current;

    if (
      match.team(self.teamId).designatedPlayer !== self ||
      match.designatedPossessionPlayer !== self ||
      anim.touchFrame !== -1 ||
      variable(anim.clip, 'incoming_special_state') !== '' ||
      variable(anim.clip, 'outgoing_special_state') !== '' ||
      !match.inPlay ||
      match.inSetPiece ||
      match.ballRetainer !== null
    ) {
      return out;
    }

    const image = match.mentalImageFor(self);
    const timeToBallMs = self.timeNeeded.usualMs;
    const animTimeMs = anim.clip.frameCount * 10;
    const futureTimeMs = Math.max(animTimeMs + DEFAULT_TOUCH_OFFSET_MS, timeToBallMs);

    const predictedOutgoingMovement = calculateOutgoingMovement(anim.positions);
    const ballPos = image.ballPrediction(futureTimeMs, _ballProbe);
    const ffo = rotate2D(
      frontOfFootOffsetRel(
        vecLength(predictedOutgoingMovement),
        anim.clip.outgoingBodyAngle,
        ballPos.z,
        _ffoScratch,
      ),
      this.nextStartAngle,
      _ffoScratch,
    );
    const desiredBallPos = _desiredBall.copy(this.nextStartPos).add(ffo);

    const toDesired = _toDesired.set(0, 0, 0);
    if (!self.hasPossession) {
      // Move onto the line the ball is travelling along, at the point nearest to where he
      // would like the ball to be. This is what makes a player cut a pass off rather than
      // chase it.
      const from = _lineA.copy(image.ballPrediction(0, _ballProbeB)).setZ(0);
      const to = _lineB.copy(ballPos).setZ(0);
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const lineLength = Math.hypot(dx, dy);
      if (lineLength < 0.5) return out; // the ball is slow, or already here

      const u = ((desiredBallPos.x - from.x) * dx + (desiredBallPos.y - from.y) * dy) / (lineLength * lineLength);
      toDesired.set(from.x + dx * u - desiredBallPos.x, from.y + dy * u - desiredBallPos.y, 0);
    } else {
      toDesired.set(ballPos.x - desiredBallPos.x, ballPos.y - desiredBallPos.y, 0);
    }

    // If the ball is much further away than this clip can help with, do nothing and let the
    // next clip deal with it — otherwise he overruns.
    let maxEffectTimeThresholdMs = 250 + DEFAULT_TOUCH_OFFSET_MS;
    if (anim.clip.outgoingVelocityEnum === Velocity.Idle) maxEffectTimeThresholdMs = 2000;
    if (futureTimeMs - (animTimeMs + DEFAULT_TOUCH_OFFSET_MS) > maxEffectTimeThresholdMs) return out;

    const toDesiredMovement = _scratch.copy(toDesired).divideScalar(animTimeMs * 0.001);
    const resultingVelocity = vecLength(
      _scratchB.copy(predictedOutgoingMovement).add(toDesiredMovement),
    );
    // Never let the nudge make him faster than he should be — it must only ever redirect.
    if (
      resultingVelocity > vecLength(predictedOutgoingMovement) &&
      resultingVelocity > DRIBBLE_WALK_SWITCH
    ) {
      return out;
    }

    normalizedMax(toDesired, MAX_SMUGGLE_MPS * (anim.clip.effectiveFrameCount * 0.01), toDesired);
    // Leave a few centimetres on the table: being exactly right looks mechanical.
    const removeDistance = 0.06;
    const length = vecLength(toDesired);
    if (length === 0) return out;
    out.copy(toDesired).multiplyScalar(Math.max(0, length - removeDistance) / length);
    out.z = 0;
    return out;
  }

  /**
   * The frame the foot arrives — humanoid.cpp:390-646.
   *
   * How well the touch was timed decides how much of it happens. `bumpyRideBias` is how
   * badly he missed, from 0 (perfect) to 1 (missed by the full 40 cm), and it blends the
   * intended touch back toward simply leaving the ball alone. A half-missed trap therefore
   * half-works, which is exactly what a scuffed touch looks like.
   */
  private handleBallContact(match: Match, self: MatchPlayer): void {
    const anim = this.current;

    // A player running straight through a loose ball nudges it whether he meant to or not
    // (humanoid.cpp:342-388).
    if (
      match.designatedPossessionPlayer === self &&
      anim.touchFrame === -1 &&
      anim.functionType === 'movement' &&
      self.lastTouchBias(1500, match.timeMs) <= 0.01 &&
      match.opponentTeam(self.teamId).lastTouchBias(240, match.timeMs) <= 0.01 &&
      match.ball.predict(0, _ballProbe).z < 1.6
    ) {
      const ballNow = _ball2D.copy(match.ball.predict(0, _ballProbe)).setZ(0);
      const ballSoon = _ball2Db.copy(match.ball.predict(200, _ballProbeB)).setZ(0);
      const distanceNow = ballNow.distanceTo(this.spatial.position);
      const distanceFuture = ballSoon.distanceTo(
        _scratch.copy(this.spatial.position).addScaledVector(this.spatial.movement, 0.2),
      );
      if (distanceNow < 0.6 && distanceFuture > 0.65 && distanceFuture > distanceNow) {
        this.applyTouch(match, self, 'trap', 0, 0.2);
        return;
      }
    }

    if (anim.touchFrame !== anim.frameNum) return;

    const touch = anim.clip.touches[0];
    if (!touch) return;
    const desiredBallHeight = touch.position.z;

    let touchableDistance = TOUCHABLE_DISTANCE;
    // Where the foot actually is: the spot it was aimed at when the clip was chosen, plus
    // every shove this clip has taken since (`humanoid.cpp:398`). It is the ONE place that
    // reads the per-clip shove rather than the player's own capped, fading one; this used to
    // read the capped one, which cannot exceed ten centimetres and is half gone in a seventh
    // of a second, so a man barged in a crowd was still judged from where he had meant to be.
    let fullBallDistance = match.ball
      .predict(0, _ballProbe)
      .distanceTo(_scratch.copy(anim.touchPos).add(anim.positionOffset));
    // A keeper already holding the ball cannot miss it.
    if (variable(anim.clip, 'incoming_retain_state') !== '') {
      fullBallDistance = 0;
      touchableDistance = 1.0;
    }

    let bumpyRideBias = clamp(fullBallDistance / touchableDistance - 0.001, 0, 1);
    bumpyRideBias = curveTwice(bumpyRideBias);

    if (
      fullBallDistance < touchableDistance &&
      Math.abs(desiredBallHeight - match.ball.predict(0, _ballProbe).z) < 1.0
    ) {
      const spinDamp = anim.functionType === 'ballcontrol' && self.hasPossession ? 0.6 : 0.5;
      this.applyTouch(match, self, anim.functionType, bumpyRideBias, spinDamp);
    }
  }

  /** Work out the touch vector for this function type and give it to the ball. */
  private applyTouch(
    match: Match,
    self: MatchPlayer,
    functionType: string,
    bumpyRideBias: number,
    spinDamp: number,
  ): void {
    const anim = this.current;
    const command = anim.originatingCommand;
    if (!command) return;

    const nextBodyAngle =
      this.startAngle +
      anim.clip.outgoingAngle +
      anim.clip.outgoingBodyAngle +
      anim.rotationSmuggleEnd;

    const touchContext: TouchContext = {
      ballPosition: match.ball.predict(0, _ballProbe).clone(),
      ballMovement: match.ball.movement(_ballMovement).clone(),
      nextStartPos: this.nextStartPos,
      nextStartAngle: this.nextStartAngle,
      nextBodyAngle,
      outgoingMovement: calculateOutgoingMovement(anim.positions),
      spatial: this.spatial,
      positionOffset: this.player.decayingPositionOffset,
      clipOutgoingVelocity: anim.clip.outgoingVelocity,
      effectiveFrameCount: anim.clip.effectiveFrameCount,
      frameNum: anim.frameNum,
      desiredDirection: command.desiredDirection,
      desiredVelocityFloat: command.desiredVelocityFloat,
      // The wish of the moment, not the wish this clip was started for. Both were the latter
      // until now, which collapsed the blend downstream and made the live three tenths of a
      // carrying touch do nothing at all.
      controllerVelocityFloat: self.controllerVelocityFloat,
      hasPossession: self.hasPossession,
      maxVelocity: this.player.maxVelocity,
      stat: (name) => this.player.stat(name),
      closestOpponentDistance: self.closestOpponentDistance,
    };

    const spin: TouchSpin = { x: 0, y: 0, z: 0 };
    const touchVec = _touchVec.set(0, 0, 0);

    // Intercepting an opponent's pass is harder than collecting your own team's.
    const opponents = match.opponentTeam(self.teamId);
    const interceptBias =
      match.lastTouchTeamId !== self.teamId && opponents.lastTouchPlayer
        ? opponents.lastTouchPlayer.lastTouchBias(
            1000 - this.player.stat('physical_reaction') * 500,
            match.timeMs,
          )
        : 0;

    // How much of the new spin the ball takes: 1 replaces what it was carrying, 0 leaves it
    // alone. Every contact type but one picks a constant and fades it out as the touch is
    // mistimed — a badly missed kick barely changes the spin either. Null means this contact
    // does not touch the spin at all.
    let appliedSpinBias: number | null = spinDamp * (1 - bumpyRideBias);
    let touchType = isKickedBodyPart(anim.clip.touchBodyPart)
      ? TouchType.IntentionalKicked
      : TouchType.IntentionalNonkicked;
    let shouldMoveBall = true;

    if (functionType === 'trap' || (functionType === 'ballcontrol' && !self.hasPossession)) {
      trapVector(touchContext, interceptBias, this.random, spin, touchVec);
    } else if (functionType === 'ballcontrol') {
      ballControlVector(touchContext, command.frontOfFootOffset ?? 0, spin, touchVec);
    } else if (
      functionType === 'shortpass' ||
      functionType === 'longpass' ||
      functionType === 'highpass'
    ) {
      const direction = command.touchInfo?.desiredDirection ?? command.desiredDirection;
      const power = command.touchInfo?.desiredPower ?? 0;
      rotate2D(
        vectorVariable(anim.clip, 'balldirection', _animBallDirection),
        this.startAngle + anim.rotationSmuggleOffset,
        _animBallDirection,
      );
      passVector(
        touchContext,
        functionType,
        direction,
        power,
        numericVariable(anim.clip, 'touch_maxpowerfactor'),
        numericVariable(anim.clip, 'touch_difficultyfactor'),
        _animBallDirection,
        interceptBias,
        this.random,
        spin,
        touchVec,
      );
      appliedSpinBias = 0.9 * (1 - bumpyRideBias);
    } else if (functionType === 'shot') {
      // Aim the shot at the spot it was decided for, from where the ball has actually ended
      // up. The direction was settled when the strike was committed to, up to a quarter of a
      // second ago and a couple of metres back; `refinedShotDirection` recovers the point on
      // the goal line it was pointing at and re-aims at that, within eighteen degrees.
      //
      // The lead has to be measured from the same place the aim was. A computer player picks
      // his spot from where he will be a fifth of a second later (`shotCommand` in
      // `src/sim/controller.ts`); a person aims from where he is standing.
      const committed = command.touchInfo?.desiredDirection ?? command.desiredDirection;
      const aimLead = match.humanControlled(self) ? 0 : SHOT_AIM_LEAD_SECONDS;
      const direction = refinedShotDirection(
        committed,
        _aimOrigin.copy(this.startPos).addScaledVector(anim.incomingMovement, aimLead),
        touchContext.ballPosition,
        match.team(self.teamId).side,
        _shotDirection,
      );
      const power = command.touchInfo?.desiredPower ?? 0;
      rotate2D(
        movementAtFrame(anim.clip.positionCache, anim.frameNum, 0, _touchMovement),
        this.spatial.angle,
        _touchMovement,
      );
      shotVector(
        touchContext,
        _touchMovement,
        direction,
        power,
        numericVariable(anim.clip, 'touch_maxpowerfactor'),
        this.random,
        spin,
        touchVec,
        command.touchInfo?.confidenceBonus ?? 0,
      );
      appliedSpinBias = 0.7 * (1 - bumpyRideBias);
    } else if (functionType === 'interfere') {
      interfereVector(touchContext, interceptBias, this.random, spin, touchVec);
      // The odd one out, and it is not a typo of ours. `Ball::SetRotation` takes four
      // arguments — the three spin axes and then the blend (`ball.hpp:64`, the blend
      // defaulting to 1) — and every other contact passes all four (humanoid.cpp:382, 439,
      // 457, 541, 577, 627). The standing tackle passes THREE (humanoid.cpp:594), so its
      // 0.3 is the vertical spin and the blend falls back to 1: a ball scrambled away by a
      // challenge loses whatever spin it arrived with instead of keeping seven tenths of it
      // and carrying on curving the way the passer struck it.
      spin.z = 0.3 * (1 - bumpyRideBias);
      appliedSpinBias = 1;
      touchType = TouchType.Accidental;
    } else if (functionType === 'deflect') {
      const relativeVelocityDifficulty = normalizedClamp(
        _relativeVelocity.copy(touchContext.ballMovement).sub(self.movement).length(),
        0,
        40,
      );
      const lastOpponentTouch = opponents.lastTouchPlayer;
      const reactionDifficulty = lastOpponentTouch
        ? Math.pow(
            lastOpponentTouch.lastTouchBias(
              1200 - this.player.stat('physical_reaction') * 400,
              match.timeMs,
            ),
            0.6,
          )
        : 0;
      // The same number twice: how nearly he could have held it. Past 0.3 he catches it, and
      // below that it decides whether the save is a punch he directed or a hand he got across
      // a ball that was going past him either way. Scaled by that same 0.3 so that a save he
      // only just failed to hold still comes out as a punch.
      const heldness = (1 - relativeVelocityDifficulty) * (1 - reactionDifficulty);
      const canRetain =
        variable(anim.clip, 'outgoing_retain_state') !== '' &&
        match.ballRetainer === null &&
        heldness >= 0.3;

      if (canRetain) {
        match.ballRetainer = self;
        shouldMoveBall = false;
      } else {
        deflectVector(
          touchContext.ballMovement,
          this.spatial.movement,
          match.team(self.teamId).side,
          heldness / 0.3,
          touchContext.ballPosition.y,
          this.random,
          touchVec,
        );
      }
      appliedSpinBias = 0.2 * (1 - bumpyRideBias);
      touchType = TouchType.Accidental;
    } else if (functionType === 'sliding') {
      slidingVector(
        vectorVariable(anim.clip, 'balldirection', _animBallDirection),
        this.spatial.angle,
        touchContext.ballMovement,
        touchVec,
      );
      appliedSpinBias = null;
      touchType = TouchType.Accidental;
    } else {
      // Trip and special clips never have a ball-contact branch in Humanoid::Process.
      return;
    }

    if (shouldMoveBall) {
      touchVec
        .multiplyScalar(1 - bumpyRideBias)
        .addScaledVector(touchContext.ballMovement, bumpyRideBias);
      match.ball.touch(touchVec);
      if (appliedSpinBias !== null) {
        match.ball.setRotation(spin.x, spin.y, spin.z, appliedSpinBias);
      }
      match.refreshBallPredictions();
    }
    match.setLastTouchPlayer(self, touchType);
  }

  /** humanoid.cpp:668-742 — this frame's share of each whole-body offset. */
  private updateSmuggles(): void {
    const anim = this.current;
    const clip = anim.clip;

    // Action smuggle: spread the nudge over the frames before contact, weighted so most of
    // it happens in the middle. Ten per cent of it is linear so the very first and last
    // frames still contribute.
    if (anim.touchFrame !== -1 && anim.frameNum <= anim.touchFrame) {
      const span = anim.touchFrame + 1;
      let value = Math.cos((anim.frameNum / span - 0.5) * Math.PI * 2) + 1;
      value = value * 0.1 + 0.9;
      this.spatial.actionSmuggleMovement
        .copy(anim.actionSmuggle)
        .multiplyScalar((value / span) * 100);
      anim.actionSmuggleOffset.addScaledVector(this.spatial.actionSmuggleMovement, 1 / 100);
    } else {
      this.spatial.actionSmuggleMovement.set(0, 0, 0);
    }

    // Movement smuggle: the same shape, spread over the whole clip, and only for a clip
    // that does NOT touch the ball — otherwise it would move the foot away from the
    // contact the action smuggle just lined up.
    if (anim.touchFrame === -1 && anim.frameNum <= clip.effectiveFrameCount) {
      const span = clip.effectiveFrameCount + 1;
      let value = Math.cos((anim.frameNum / span - 0.5) * Math.PI * 2) + 1;
      value = value * 0.1 + 0.9;
      this.spatial.movementSmuggleMovement
        .copy(anim.movementSmuggle)
        .multiplyScalar((value / span) * 100);
      anim.movementSmuggleOffset.addScaledVector(this.spatial.movementSmuggleMovement, 1 / 100);
    } else {
      this.spatial.movementSmuggleMovement.set(0, 0, 0);
    }

    // Rotation smuggle: blend the incoming body-angle error out and the steering
    // correction in. On a clip that touches the ball, the steering correction is held back
    // entirely until after contact, so the turn cannot spoil the touch.
    const cappedFrameBias = Math.min(
      1.0,
      (anim.frameNum + 1) / Math.min(BEGIN_ROTATION_FRAME_COUNT, clip.effectiveFrameCount + 1),
    );
    let beginFrameBias = cappedFrameBias;
    let endFrameBias = cappedFrameBias;
    if (anim.touchFrame !== -1) {
      beginFrameBias = Math.min(
        1.0,
        (anim.frameNum + 1) / Math.min(BEGIN_ROTATION_FRAME_COUNT, anim.touchFrame + 1),
      );
      endFrameBias =
        anim.frameNum > anim.touchFrame
          ? (anim.frameNum - anim.touchFrame) / (clip.effectiveFrameCount - anim.touchFrame)
          : 0;
    }
    anim.rotationSmuggleOffset =
      anim.rotationSmuggleBegin * (1 - beginFrameBias) + anim.rotationSmuggleEnd * endFrameBias;
  }

  /** humanoid.cpp:765-780 — what the renderer will draw this frame. */
  private publish(): void {
    this.buffer.clip = this.current.clip;
    this.buffer.frameNum = this.current.frameNum;
    this.buffer.orientation = this.startAngle + this.current.rotationSmuggleOffset;
    this.buffer.noPos = this.current.positions.length > this.current.frameNum;
  }

  /** The world position the body should be drawn at this frame. */
  worldPosition(out = new Vector3()): Vector3 {
    const anim = this.current;
    out.copy(this.startPos);
    if (anim.positions.length > anim.frameNum) out.add(anim.positions[anim.frameNum]!);
    out.add(anim.actionSmuggleOffset).add(anim.movementSmuggleOffset);
    return out;
  }

  /** Write the current frame's pose onto a skeleton. */
  writePose(skeleton: Skeleton, timeDiffMs = 10): void {
    this.buffer.timeDiffMs = clamp(timeDiffMs, 10, 50);
    applyPose(skeleton, this.buffer, this.history);
  }

  /** Attach a retained ball to the exact body part named by the clip's retain state. */
  retainedBallPosition(skeleton: Skeleton, out = new Vector3()): Vector3 | null {
    const bodyPart =
      variable(this.current.clip, 'outgoing_retain_state') ||
      variable(this.current.clip, 'incoming_retain_state');
    if (!bodyPart) return null;
    const joint = skeleton.indexOf(bodyPart);
    out.copy(skeleton.worldPosition[joint]!).multiplyScalar(this.height / 1.92);
    return out.add(this.worldPosition(_retainedRoot));
  }

  /** The quadrant the current clip finishes in — useful for debugging and the HUD. */
  get quadrantId(): number {
    return this.current.clip.quadrantId;
  }

  /**
   * How hard the pose is being dragged back toward the previous one while the current clip
   * gets going: 0 none at all, 1 the full amount (`humanoid.cpp:277-284`). Exposed so it can
   * be inspected and tested; the renderer reads it off the buffer, not through here.
   */
  get poseSmoothFactor(): number {
    return this.buffer.smoothFactor;
  }
}

/** humanoid.cpp:418-419 — the S-curve applied twice, at full then half strength. */
function curveTwice(value: number): number {
  const once = Math.sin((value - 0.5) * Math.PI) * 0.5 + 0.5;
  const bias = 0.5;
  return (Math.sin((once - 0.5) * Math.PI) * 0.5 + 0.5) * bias + once * (1 - bias);
}

function tripCommands(direction: Vector3, type: number, velocity: number): PlayerCommand[] {
  const types = type === 1 ? [1] : [type, type === 3 ? 2 : 3, 1];
  const commands = types.map((tripType): PlayerCommand => ({
    desiredFunctionType: 'trip',
    useDesiredMovement: false,
    desiredDirection: direction.clone(),
    desiredVelocityFloat: velocity,
    useDesiredLookAt: false,
    desiredLookAt: new Vector3(),
    desiredTripDirection: direction.clone(),
    tripType,
  }));
  commands.push({
    desiredFunctionType: 'movement',
    useDesiredMovement: true,
    desiredDirection: direction.clone(),
    desiredVelocityFloat: velocity,
    useDesiredLookAt: false,
    desiredLookAt: new Vector3(),
  });
  return commands;
}

/**
 * `specialvar1` for the reaction to being struck — tranche 2.3 of
 * `docs/design/violence-and-variety.md`. 1 through 5 are already taken (the two happy
 * celebrations, the sad one, the referee's card, the punch and the flying kick — see
 * `controller.ts`'s own `CELEBRATION_*` constants and `assets/animations/special/`), so this
 * starts at 6.
 */
export const STRUCK_REACTION = 6;
/** `specialvar2`: which of the two clips, exactly the celebration's own pattern. */
export const STRUCK_STANDING = 1;
export const STRUCK_RUNNING = 2;

/**
 * Two clips, not one, and which is asked for is read off the VICTIM's own velocity at
 * promotion time rather than passed in by the caller — `struck` only records that he was
 * hit and which way. The reason is the same one the celebration comment gives: the hard
 * filter's incoming-velocity gate refuses to start a clip drawn at speed on a standing man
 * and refuses a standing clip to one already sprinting (`select.ts`'s crude selection, stage
 * (b)), so asking for the wrong one selects nothing at all. Both are tried, primary first,
 * so a man caught at a dribble or a walk — a speed neither clip was drawn at — still falls
 * through to the plain movement command rather than being re-placed.
 */
function struckCommands(
  direction: Vector3,
  incomingVelocity: Velocity,
  velocity: number,
): PlayerCommand[] {
  const primary = incomingVelocity === Velocity.Sprint ? STRUCK_RUNNING : STRUCK_STANDING;
  const secondary = primary === STRUCK_STANDING ? STRUCK_RUNNING : STRUCK_STANDING;
  const commands: PlayerCommand[] = [primary, secondary].map((register): PlayerCommand => ({
    desiredFunctionType: 'special',
    // Both false, exactly as the goal celebration's own command: a special clip is played
    // exactly as drawn, so there is nothing here for the solver to bend.
    useDesiredMovement: false,
    desiredDirection: direction.clone(),
    desiredVelocityFloat: velocity,
    useDesiredLookAt: false,
    desiredLookAt: new Vector3(),
    specialVar1: STRUCK_REACTION,
    specialVar2: register,
  }));
  commands.push({
    desiredFunctionType: 'movement',
    useDesiredMovement: true,
    desiredDirection: direction.clone(),
    desiredVelocityFloat: velocity,
    useDesiredLookAt: false,
    desiredLookAt: new Vector3(),
  });
  return commands;
}

const _scratch = new Vector3();
const _scratchB = new Vector3();
const _ball2D = new Vector3();
const _ball2Db = new Vector3();
const _ballProbe = new Vector3();
const _ballProbeB = new Vector3();
const _ballIncoming = new Vector3();
const _ballMovement = new Vector3();
const _touchVec = new Vector3();
const _animBallDirection = new Vector3();
const _aimOrigin = new Vector3();
const _shotDirection = new Vector3();
const _touchMovement = new Vector3();
const _relativeVelocity = new Vector3();
const _smuggleOut = new Vector3();
const _ffoScratch = new Vector3();
const _desiredBall = new Vector3();
const _toDesired = new Vector3();
const _lineA = new Vector3();
const _lineB = new Vector3();
const _retainedRoot = new Vector3();

/** Re-exported so callers can label a movement without importing the quantiser. */
export { getQuadrantID };
export { floatToEnumVelocity };
